import { useRef } from "react";
import { useAutoScroll } from "./OurWork";
import { ArrowRight, CalendarHeart, ChevronRight, Loader2, Sparkles, Star } from "lucide-react";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@/components/ui/drawer";
import { useServices } from "@/lib/useServices";
import { useContent } from "@/lib/content";
import { Reveal, SectionHead } from "./Reveal";
import { BookLink } from "@/lib/fresha";

const minPrice = (items) => Math.min(...items.map((s) => Number(s.price.replace(/[^0-9.]/g, ""))));

const CategoryTile = ({ cat, onOpen }) => (
  <button className="cat-tile" onClick={() => onOpen(cat.id)} data-testid={`price-category-${cat.id}`}>
    <span className="cat-name">{cat.label}</span>
    <span className="cat-meta">{cat.items.length} services · from ${minPrice(cat.items)}</span>
    <ChevronRight size={16} className="cat-arrow" />
  </button>
);

const PriceSheet = ({ cat, onClose, onBook }) => (
  <Drawer open={!!cat} onOpenChange={(o) => !o && onClose()} shouldScaleBackground={false}>
    <DrawerContent className="sheet" data-testid="price-sheet">
      {cat && (
        <>
          <div className="sheet-head">
            <p className="eyebrow"><Star size={11} className="gold-ic" /> Wax Moi menu</p>
            <DrawerTitle className="sheet-title" data-testid="price-sheet-title">{cat.label}</DrawerTitle>
            <DrawerDescription className="sheet-desc">Tap a service to request it. Prices in BSD.</DrawerDescription>
          </div>
          <div className="sheet-body" data-testid={`price-panel-${cat.id}`}>
            {cat.items.map((s) => (
              <button className="price-row sheet-row" key={s.key} onClick={() => onBook(s.key)} data-testid="price-row">
                <span>{s.name}<small className="dur">{s.duration}</small>{s.description && <small className="price-desc" data-testid="price-row-description">{s.description}</small>}</span>
                <span className="row-right"><strong>{s.price}</strong><span className="row-book">Book <ArrowRight size={12} /></span></span>
              </button>
            ))}
            <p className="price-note confirm" data-testid="price-confirm-note">Please confirm current pricing when booking.</p>
          </div>
          <div className="sheet-foot">
            <button className="btn gold" onClick={() => onBook("")} data-testid="price-sheet-book-button"><CalendarHeart size={16} /> Book an appointment</button>
            <button className="btn light" onClick={onClose} data-testid="price-sheet-close-button">Close</button>
          </div>
        </>
      )}
    </DrawerContent>
  </Drawer>
);

const Specials = ({ cat, onBook }) => {
  const ref = useRef(null);
  const items = cat?.items || [];
  useAutoScroll(ref, items.length > 0);
  if (!items.length) return null;
  return (
    <Reveal className="specials"><div data-testid="specials-strip">
      <p className="specials-title"><Sparkles size={14} /> {cat.label} &amp; deals</p>
      <div className="specials-track" ref={ref}>
        {[...items, ...items].map((s, i) => (
          <button key={`${s.key}-${i}`} type="button" className="special-card" onClick={() => onBook(s.key)} tabIndex={i >= items.length ? -1 : 0}
            data-testid={i < items.length ? `special-card-${i}` : undefined}>
            <span className="special-name">{s.name}</span>
            <span className="special-meta">{s.duration}</span>
            <span className="special-foot"><strong>{s.price}</strong><span className="row-book">Book <ArrowRight size={12} /></span></span>
          </button>
        ))}
      </div>
    </div></Reveal>
  );
};

export const Prices = ({ open, setOpen, onBook }) => {
  const { data, isLoading } = useServices();
  const t = useContent().data.prices;
  const cats = data?.categories || [];
  const cat = cats.find((c) => c.id === open);

  return (
    <section className="section menu" id="prices" data-testid="prices-section">
      <div className="wrap">
        <SectionHead eyebrow={t.eyebrow} title={t.title} text={t.text} />
        {isLoading && <p className="muted-sm center" data-testid="prices-loading"><Loader2 size={16} className="spin" /> Loading menu…</p>}
        <Specials cat={cats.find((c) => c.id === "specials")} onBook={onBook} />
        <Reveal className="cat-grid">
          {cats.map((c) => <CategoryTile key={c.id} cat={c} onOpen={setOpen} />)}
        </Reveal>
        <div className="center-cta">
          <BookLink className="btn gold" testId="prices-book-button">Book an appointment</BookLink>
        </div>
      </div>
      <PriceSheet cat={cat} onClose={() => setOpen(null)} onBook={onBook} />
    </section>
  );
};
